import React from 'react';
import { Link } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import PageContainer from './kit/PageContainer';
import { useIncomeCalculator } from './income-calculator/useIncomeCalculator';
import { LoanTypeSelector } from './income-calculator/LoanTypeSelector';
import { AgencyComparison } from './income-calculator/AgencyComparison';
import { IncomeFormsCard } from './income-calculator/forms/IncomeFormsCard';
import { DocDropZone } from './income-calculator/DocDropZone';
import { AnalysisResults } from './income-calculator/AnalysisResults';
import { HybridCalculation } from './income-calculator/HybridCalculation';
import { SendToBorrowerModal } from './income-calculator/SendToBorrowerModal';
import { ManualCalculator } from './income-calculator/ManualCalculator';
import { RecentCalculations } from './income-calculator/RecentCalculations';
import { CalcDetailDrawer } from './income-calculator/CalcDetailDrawer';
import { OpenUploadLinks } from './income-calculator/OpenUploadLinks';
import { WaitingSsnReviews } from './income-calculator/WaitingSsnReviews';

export default function IncomeCalculatorPage() {
  const {
    type, setType, form, setForm, setNum, result, busy, calc,
    history, del, detailCalcId, setDetailCalcId,
    loanType, setLoanType, agencyRules, currentRules,
    dragging, analyzing, analysis, setAnalysis, requestingDocs, requestVariableIncomeDocs,
    sendToBorrower, setSendToBorrower, fileInputRef, analyzeDocs, reanalyze, onDrop, onDragOver, onDragLeave,
    waiting, readReviewed, afterWaitingReview,
    loanCtx, saveToLoan, savingToLoan, savedToLoan,
  } = useIncomeCalculator();

  return (
    <>
    <PageContainer
      className="max-w-[1400px] mx-auto"
      title={<span className="inline-flex items-center gap-2">Income Calculator <Sparkles size={22} className="text-brand-accent" /></span>}
      subtitle="Drop paystubs, W-2s, tax returns, K-1s, VOEs — Audrey classifies each file automatically, extracts every pay bucket, and computes qualifying monthly income."
    >

      {loanCtx && (
        <div className="flex items-center justify-between gap-3 flex-wrap rounded-xl border border-brand-accent/40 bg-[#F3FAE8] px-4 py-3 text-[13px]" data-testid="income-loan-banner">
          <div>
            Analyzing income for <b className="text-brand-navy">{loanCtx.borrower || 'this loan'}</b>. Run a calculation, then save it to the loan file.
          </div>
          <Link to={`/portal/loans/${loanCtx.loanId}?section=financial_info`} className="font-semibold text-brand-navy hover:underline" data-testid="income-loan-back">Back to the loan file</Link>
        </div>
      )}

      {/* Agency / loan-program selector — drives self-employed add-back & averaging rules */}
      <LoanTypeSelector
        loanType={loanType}
        setLoanType={setLoanType}
        rules={agencyRules}
        currentRules={currentRules}
      />

      {/* Program Bakeoff — side-by-side Form 91 across all 5 agencies */}
      <AgencyComparison />

      {/* Form 1084 / 91 / 92 worksheets (ticket 215) */}
      <IncomeFormsCard />

      {/* AI Multi-File Drop Zone */}
      <DocDropZone
        dragging={dragging}
        analyzing={analyzing}
        fileInputRef={fileInputRef}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        analyzeDocs={analyzeDocs}
      />

      {/* Held files the server still has: they survive a page refresh (ticket 176) */}
      <WaitingSsnReviews items={waiting} busy={analyzing} onReviewed={afterWaitingReview} onRead={readReviewed} />

      {/* Analysis result */}
      {analysis && <AnalysisResults analysis={analysis} onDismiss={() => setAnalysis(null)} onRequestDocs={requestVariableIncomeDocs} requestingDocs={requestingDocs} onSendToBorrower={() => setSendToBorrower(true)} onReviewed={reanalyze} />}
      {analysis?.hybrid && <HybridCalculation hybrid={analysis.hybrid} />}

      {/* Manual calculator */}
      <ManualCalculator
        type={type}
        setType={setType}
        form={form}
        setForm={setForm}
        setNum={setNum}
        result={result}
        busy={busy}
        calc={calc}
        loanCtx={loanCtx}
        saveToLoan={saveToLoan}
        savingToLoan={savingToLoan}
        savedToLoan={savedToLoan}
      />

      <RecentCalculations history={history} setDetailCalcId={setDetailCalcId} del={del} />

      {/* Upload links sent from here that still work (ticket 211); reloads when the send modal closes */}
      <OpenUploadLinks key={sendToBorrower ? 'sending' : 'idle'} />
    </PageContainer>

    {/* Overlays live outside PageContainer so its space-y-6 never offsets a fixed/portaled element */}
    {sendToBorrower && <SendToBorrowerModal analysis={analysis} onClose={() => setSendToBorrower(false)} />}
    {detailCalcId && (
      <CalcDetailDrawer calcId={detailCalcId} onClose={() => setDetailCalcId(null)} />
    )}
    </>
  );
}
